import api from "@/app/api";
import Guid from "@/app/types/common/guid";
import { Instant, LocalDate, LocalTime } from "@js-joda/core";
import {ModelObject, ModelObjectWithDirtyCheck, type ValidationRule} from "@/app/services/modelObject";
import ServiceBase from "@/app/services/serviceBase";
import converters from "@/app/services/converters"
import { EnumDayOfWeek } from "./enums/enumDayOfWeek.generated";

// Model Objects

export class AddressReadOnly extends ModelObject<AddressReadOnly> {

    private _latitude: number | null = null;
    public get latitude(): number | null { return this._latitude }
    public set latitude(value: number | null) {
      this._latitude = value === undefined ? null : value;
    }
    private _longitude: number | null = null;
    public get longitude(): number | null { return this._longitude }
    public set longitude(value: number | null) {
      this._longitude = value === undefined ? null : value;
    }

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.latitude = jsonSerializedObject.latitude == null ? null : jsonSerializedObject.latitude;
          this.longitude = jsonSerializedObject.longitude == null ? null : jsonSerializedObject.longitude;
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.latitude = this.latitude == null ? null : this.latitude;
        toRet.longitude = this.longitude == null ? null : this.longitude;
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
    ];
}


export class Address extends ModelObject<Address> {

    public street1: string = "";
    public street2: string = "";
    public street3: string = "";
    public townCity: string = "";
    public province: string = "";
    public country: string = "";
    public postcode: string = "";
    public readOnly: AddressReadOnly = new AddressReadOnly();

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.street1 = jsonSerializedObject.street1;
          this.street2 = jsonSerializedObject.street2;
          this.street3 = jsonSerializedObject.street3;
          this.townCity = jsonSerializedObject.townCity;
          this.province = jsonSerializedObject.province;
          this.country = jsonSerializedObject.country;
          this.postcode = jsonSerializedObject.postcode;
          this.readOnly = new AddressReadOnly(jsonSerializedObject.readOnly);
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.street1 = this.street1;
        toRet.street2 = this.street2;
        toRet.street3 = this.street3;
        toRet.townCity = this.townCity;
        toRet.province = this.province;
        toRet.country = this.country;
        toRet.postcode = this.postcode;
        toRet.readOnly = this.readOnly.toJsonSerializedObject();
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "readOnly", rule: (v: any) => !!v || "readOnly is required"},
    ];
}


export class SalesUserDateSettings extends ModelObject<SalesUserDateSettings> {

    public version: number = 0;
    /**
    * User to which this settings belongs to.
    */
    public userId: Guid = Guid.createEmpty();
    /**
    * The date for which this settings belongs to.
    */
    public date: LocalDate = LocalDate.ofEpochDay(0);
    /**
    * The start time of the user's working hours for the day.
    */
    public startTime: LocalTime = LocalTime.MIDNIGHT;
    /**
    * The end time of the user's working hours for the day.
    */
    public endTime: LocalTime = LocalTime.MIDNIGHT;
    public address: Address = new Address();

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.version = jsonSerializedObject.version;
          this.userId = Guid.parseOrEmpty(jsonSerializedObject.userId);
          this.date = LocalDate.parse(jsonSerializedObject.date);
          this.startTime = LocalTime.parse(jsonSerializedObject.startTime);
          this.endTime = LocalTime.parse(jsonSerializedObject.endTime);
          this.address = new Address(jsonSerializedObject.address);
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.version = this.version;
        toRet.userId = this.userId.toString();
        toRet.date = this.date.toString();
        toRet.startTime = this.startTime.toString();
        toRet.endTime = this.endTime.toString();
        toRet.address = this.address.toJsonSerializedObject();
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "version", rule: (v: any) => v == null || v >= -9223372036854775808 || "Version is not greater than minimum allowed value: -9223372036854775808"},
            {field: "version", rule: (v: any) => v == null || v <= 9223372036854775807 || "Version is above maximum allowed value: 9223372036854775807"},
            {field: "userId", rule: (v: any) => !v.isEmpty() || "SalesUserDateSettings.UserId cannot be empty"},
            {field: "address", rule: (v: any) => !!v || "address is required"},
            {field: "address", rule: (v: any) => v.validate()},
    ];
}


export class SalesUserDayOfWeekSettingsReadOnly extends ModelObject<SalesUserDayOfWeekSettingsReadOnly> {

    /**
    * User to which this schedule configuration belongs to.
    */
    public userId: Guid = Guid.createEmpty();

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.userId = Guid.parseOrEmpty(jsonSerializedObject.userId);
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.userId = this.userId.toString();
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "userId", rule: (v: any) => !v.isEmpty() || "SalesUserDayOfWeekSettingsReadOnly.UserId cannot be empty"},
    ];
}


export class SalesUserDayOfWeekSettings extends ModelObject<SalesUserDayOfWeekSettings> {

    /**
    * Day of week of this schedule configuration.
    */
    public dayOfWeek: EnumDayOfWeek = EnumDayOfWeek.none;
    /**
    * The start time of the user's working hours for the day.
    */
    public startTime: LocalTime = LocalTime.MIDNIGHT;
    /**
    * The end time of the user's working hours for the day.
    */
    public endTime: LocalTime = LocalTime.MIDNIGHT;
    public readOnly: SalesUserDayOfWeekSettingsReadOnly = new SalesUserDayOfWeekSettingsReadOnly();

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.dayOfWeek = EnumDayOfWeek.getByValue(jsonSerializedObject.dayOfWeek, this.dayOfWeek);
          this.startTime = LocalTime.parse(jsonSerializedObject.startTime);
          this.endTime = LocalTime.parse(jsonSerializedObject.endTime);
          this.readOnly = new SalesUserDayOfWeekSettingsReadOnly(jsonSerializedObject.readOnly);
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.dayOfWeek = this.dayOfWeek.value;
        toRet.startTime = this.startTime.toString();
        toRet.endTime = this.endTime.toString();
        toRet.readOnly = this.readOnly.toJsonSerializedObject();
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "readOnly", rule: (v: any) => !!v || "readOnly is required"},
    ];
}


export class SalesUserSettings extends ModelObject<SalesUserSettings> {

    public version: number = 0;
    /**
    * User to which this schedule configuration belongs to.
    */
    public userId: Guid = Guid.createEmpty();
    public address: Address = new Address();
    private _salesCodeId: Guid | null = null;
    /**
    * This field is used to restrict user's data visibility based on their assigned Sales Code.
    */
    public get salesCodeId(): Guid | null { return this._salesCodeId }
    public set salesCodeId(value: Guid | null) {
      this._salesCodeId = value === undefined ? null : value;
    }

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.version = jsonSerializedObject.version;
          this.userId = Guid.parseOrEmpty(jsonSerializedObject.userId);
          this.address = new Address(jsonSerializedObject.address);
          this.salesCodeId = jsonSerializedObject.salesCodeId == null ? null : Guid.parseOrEmpty(jsonSerializedObject.salesCodeId);
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.version = this.version;
        toRet.userId = this.userId.toString();
        toRet.address = this.address.toJsonSerializedObject();
        toRet.salesCodeId = this.salesCodeId == null ? null : this.salesCodeId.toString();
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "version", rule: (v: any) => v == null || v >= -9223372036854775808 || "Version is not greater than minimum allowed value: -9223372036854775808"},
            {field: "version", rule: (v: any) => v == null || v <= 9223372036854775807 || "Version is above maximum allowed value: 9223372036854775807"},
            {field: "userId", rule: (v: any) => !v.isEmpty() || "SalesUserSettings.UserId cannot be empty"},
            {field: "address", rule: (v: any) => !!v || "address is required"},
            {field: "address", rule: (v: any) => v.validate()},
            {field: "salesCodeId", rule: (v: any) => v == null || !v.isEmpty() || "SalesUserSettings.SalesCodeId cannot be empty"},
    ];
}


export class SalesUserConfiguration extends ModelObject<SalesUserConfiguration> {

    public userSettings: SalesUserSettings = new SalesUserSettings();
    /**
    * On read, this will always contain the days Monday through to Sunday.Any days missing from a write will be returned as a system default (00:00 to 23:59)
    */
    public dayOfWeekSettings: (SalesUserDayOfWeekSettings)[] = [];

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.userSettings = new SalesUserSettings(jsonSerializedObject.userSettings);
          this.dayOfWeekSettings = jsonSerializedObject.dayOfWeekSettings.map((v: any) => new SalesUserDayOfWeekSettings(v));
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.userSettings = this.userSettings.toJsonSerializedObject();
        toRet.dayOfWeekSettings = this.dayOfWeekSettings.map(v => v.toJsonSerializedObject());
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "userSettings", rule: (v: any) => !!v || "userSettings is required"},
            {field: "userSettings", rule: (v: any) => v.validate()},
            {field: "dayOfWeekSettings", rule: (v: any) => !!v || "dayOfWeekSettings is required"},
            {field: "dayOfWeekSettings", rule: (v: any) => {
                if (v === null) {
                    return true;
                }

                return (v as SalesUserDayOfWeekSettings[]).reduce((acc: Array<string>, curr: SalesUserDayOfWeekSettings) => acc.concat(curr.validate()), new Array<string>());
            }},
    ];
}


export class SalesUserSettingsService extends ServiceBase {

  public async getSalesUserDateSettings(userId: Guid, date: LocalDate, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<SalesUserDateSettings> {
    const req = {
    tenantId:api.tenantId.toString(),
    userId:userId.toString(),
    date:date.toString(),
    };
    const extractor = (response:any) => new SalesUserDateSettings(response.salesUserDateSettings);;
    return this.makeJsonRequest("salesUserSettings/v1/getSalesUserDateSettings", req, false, extractor, opts);
  };

  public async saveSalesUserDateSettings(salesUserDateSettings: SalesUserDateSettings, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<void> {
    const req = {
    tenantId:api.tenantId.toString(),
    salesUserDateSettings:salesUserDateSettings.toJsonSerializedObject(),
    };
    const extractor = null;
    return this.makeJsonRequest("salesUserSettings/v1/saveSalesUserDateSettings", req, true, extractor, opts);
  };

  public async getSalesUserConfiguration(userId: Guid, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<SalesUserConfiguration> {
    const req = {
    tenantId:api.tenantId.toString(),
    userId:userId.toString(),
    };
    const extractor = (response:any) => new SalesUserConfiguration(response.salesUserConfiguration);;
    return this.makeJsonRequest("salesUserSettings/v1/getSalesUserConfiguration", req, false, extractor, opts);
  };

  public async saveSalesUserConfiguration(salesUserConfiguration: SalesUserConfiguration, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<void> {
    const req = {
    tenantId:api.tenantId.toString(),
    salesUserConfiguration:salesUserConfiguration.toJsonSerializedObject(),
    };
    const extractor = null;
    return this.makeJsonRequest("salesUserSettings/v1/saveSalesUserConfiguration", req, true, extractor, opts);
  };
}

export const salesUserSettingsService = new SalesUserSettingsService();